/**
 * FASE 4.2 — PushOfferBanner
 * Banner morbido in alto della dashboard post-login che invita ad abilitare
 * le push notification.
 *
 * Visibile SOLO se:
 *   - utente loggato (gestito dal componente padre)
 *   - utente NON ha già una subscription push attiva (check via API esistente)
 *   - utente non ha già rifiutato in questa sessione (localStorage `push_offer_dismissed=true`)
 *
 * On "Abilita" → Notification.requestPermission + sub registration + POST /api/client/push/subscribe
 * On "Più tardi" → setItem localStorage e nascondi
 */
import { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useClientAuth } from "@/contexts/ClientAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;

async function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushOfferBanner() {
  const { account, getToken } = useClientAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!account) return;
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    if (window.localStorage?.getItem("push_offer_dismissed") === "true") return;
    if (Notification.permission === "denied") return;
    // Se già subscribed → nascondi
    let cancel = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (cancel) return;
        if (!sub && Notification.permission !== "granted") {
          setVisible(true);
        } else if (Notification.permission === "granted" && !sub) {
          // permesso dato ma sub mancante → riproponiamo
          setVisible(true);
        }
      } catch {
        if (!cancel) setVisible(true);  // nel dubbio, mostra
      }
    })();
    return () => { cancel = true; };
  }, [account]);

  if (!visible) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permesso negato. Puoi riabilitarlo dalle impostazioni del browser.");
        setBusy(false); return;
      }
      // VAPID public key (TEST_MODE: usiamo /api/client/push/public-key se disponibile)
      let vapidKey = null;
      try {
        const r = await axios.get(`${API}/push/public-key`);
        vapidKey = r.data?.public_key || r.data?.key || null;
      } catch {
        // fallback: lo skip provoca solo la chiusura senza push
      }
      const reg = await navigator.serviceWorker.ready;
      const subOptions = { userVisibleOnly: true };
      if (vapidKey) subOptions.applicationServerKey = await urlBase64ToUint8Array(vapidKey);
      const sub = await reg.pushManager.subscribe(subOptions);
      const token = getToken();
      await axios.post(`${API}/push/subscribe`,
        sub.toJSON(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Notifiche abilitate ✓");
      setVisible(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'abilitazione");
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { window.localStorage.setItem("push_offer_dismissed", "true"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3 mb-4"
      data-testid="push-offer-banner">
      <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-900">
          Vuoi ricevere notifiche su scadenze, preventivi e novità?
        </p>
        <p className="text-[11px] text-amber-700">
          Riceverai promemoria solo per eventi rilevanti. Niente spam.
        </p>
      </div>
      <div className="flex gap-2">
        <button onClick={enable} disabled={busy}
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-full inline-flex items-center gap-1 disabled:opacity-60"
          data-testid="push-offer-enable">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
          Abilita
        </button>
        <button onClick={dismiss}
          className="px-3 py-1.5 text-xs text-amber-800 hover:bg-amber-100 rounded-full inline-flex items-center gap-1"
          data-testid="push-offer-dismiss">
          Più tardi
        </button>
      </div>
      <button onClick={dismiss} className="text-amber-700 hover:text-amber-900 p-1 hidden sm:block"
        aria-label="Chiudi" data-testid="push-offer-x">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
